import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Wrench, Lightbulb, Activity, Building2, Home as HomeIcon, CheckCircle2, Clock, ThumbsUp, Star, Phone, Camera, FileText, DollarSign, Banknote, CreditCard, Timer, AlertTriangle } from 'lucide-react';
import { AnimatedSection, AnimatedGrid, AnimatedGridItem } from '../components/AnimatedSection';
export function Home() {
  return <div className="w-full" data-id="element-311">
      {/* HERO SECTION */}
      <section className="relative bg-navy text-white overflow-hidden pt-[72px] border-b-4 border-accent" data-id="element-312">
        <div className="relative grid grid-cols-1 md:grid-cols-12 min-h-[560px] md:min-h-[620px]" data-id="element-313">
          {/* Left — panel video */}
          <div className="md:col-span-7 relative h-72 md:h-auto z-10 shadow-2xl overflow-hidden" data-id="element-314">
            <video src="https://cdn.magicpatterns.com/uploads/vyd3LhuauEC5um1DD4PSex/20260617_114359249.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover object-left scale-[1.85] origin-left" data-id="element-315" />
          </div>

          {/* Right — content on navy */}
          <div className="md:col-span-5 bg-navy relative flex items-center px-6 md:px-10 lg:px-14 py-12 md:py-16" data-id="element-316">
            <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-317"></div>
            <AnimatedSection className="w-full max-w-xl relative z-10" data-id="element-318">
              <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/40 px-3 py-1.5 rounded-sm mb-6" data-id="element-319">
                <Zap className="w-4 h-4 text-yellow-400" fill="currentColor" data-id="element-320" />
                <span className="text-[11px] font-bold tracking-wider text-yellow-400 uppercase" data-id="element-321">
                  Licensed & Insured Electrical Contractor
                </span>
              </div>
              <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight mb-6 uppercase" data-id="element-322">
                Powering Homes and Businesses with{' '}
                <span className="text-accent" data-id="element-323">Safe, Reliable</span> Electrical
                Solutions
              </h1>
              <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed" data-id="element-324">
                Professional residential and commercial electrical services
                delivered with safety, quality workmanship, and customer
                satisfaction at the forefront.
              </p>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-325">
                <Link to="/quote" className="bg-accent hover:bg-accent-hover text-white px-6 py-3.5 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors text-center flex items-center justify-center gap-2 group" data-id="element-326">
                  Request a Free Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-327" />
                </Link>
                <a href="tel:4075040596" className="bg-transparent hover:bg-white/10 text-white border border-white/40 px-6 py-3.5 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors text-center flex items-center justify-center gap-2" data-id="element-328">
                  <Phone className="w-4 h-4" data-id="element-329" />
                  Call Now
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FEATURED HIGHLIGHTS BAR — dark navy with circular icons */}
      <section className="bg-navy border-t border-slate-800/60" data-id="element-330">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-331">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-332">
            {[{
            title: 'Financing Available',
            icon: DollarSign
          }, {
            title: 'Emergency Service Available',
            icon: Clock
          }, {
            title: 'Residential & Commercial Services',
            icon: HomeIcon
          }, {
            title: 'Free Estimates',
            icon: FileText
          }].map((highlight, i) => <div key={i} className="flex items-center gap-4" data-id="element-333">
                <div className="bg-accent w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 shadow-lg" data-id="element-334">
                  <highlight.icon className="w-6 h-6 md:w-7 md:h-7 text-white" data-id="element-335" />
                </div>
                <span className="font-display font-bold text-white text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-336">
                  {highlight.title}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SERVICES / RECENT PROJECTS */}
      <section className="py-16 md:py-20 bg-white" id="services" data-id="element-337">
        <div className="max-w-7xl mx-auto px-6" data-id="element-338">
          <AnimatedSection className="text-center mb-10" data-id="element-339">
            <p className="text-sm font-bold tracking-widest text-accent uppercase mb-2" data-id="element-340">
              Services Offered / Recent Work
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy tracking-tight uppercase mb-4" data-id="element-341">
              Real Jobs. Real Pictures. Real Success.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg" data-id="element-342">
              We don't use stock photos. Explore our actual completed projects
              to see the quality and scope of the electrical services we
              provide.
            </p>
          </AnimatedSection>

          <AnimatedGrid className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-343">
            {[{
            title: 'Residential Lighting',
            desc: 'Custom lighting solutions that enhance your space.',
            video: 'https://cdn.magicpatterns.com/uploads/1zqnor6CXioUFgkiR6vyAs/kling_20260618_VIDEO_Create_a_c_96_0.mp4'
          }, {
            title: 'Exterior Lighting',
            desc: 'Security and landscape lighting installations.',
            img: 'https://cdn.magicpatterns.com/uploads/jYK2h8BpxE8bw1s4qkiiCL/Screenshot_20260617_125015_Messages.jpg'
          }, {
            title: 'Panel Upgrades',
            desc: 'Safe, reliable panel upgrades built to handle your needs.',
            video: 'https://cdn.magicpatterns.com/uploads/xaXfSuzU2Y15vXS2dQxGWX/kling_20260618_VIDEO_Create_a_r_39_0.mp4'
          }, {
            title: 'Service Upgrades',
            desc: 'Complete exterior meter and panel upgrades.',
            img: 'https://cdn.magicpatterns.com/uploads/i88f8u14MjbiHBJpgzEgHA/IMG_20260617_124122.jpg'
          }, {
            title: 'Circuit Installation',
            desc: 'New circuits and organized panel labeling.',
            img: 'https://cdn.magicpatterns.com/uploads/ezaGxfSA6yhhp5w4fmLENV/IMG_20260617_124120.jpg'
          }, {
            title: 'Commercial Projects',
            desc: 'Dependable electrical solutions for your business.',
            video: 'https://cdn.magicpatterns.com/uploads/oCPmvT2JsVKoTGeBv4u8Pt/kling_20260618_VIDEO__Image1Cre_150_0.mp4'
          }, {
            title: 'Mini-Split Wiring',
            desc: 'Dedicated circuits for HVAC and mini-splits.',
            img: 'https://cdn.magicpatterns.com/uploads/fRghqUyumuhjTReGsq6KGo/IMG_20260617_124108.jpg'
          }, {
            title: 'Service Installations',
            desc: 'EV chargers, outlets, and more installed professionally.',
            video: 'https://cdn.magicpatterns.com/uploads/rKVH5qhdeF1wBqUzNwMXcu/kling_20260618_VIDEO_Create_a_r_105_0.mp4'
          }, {
            title: 'Main Panel Replacement',
            desc: 'Safe and reliable main breaker panel replacements.',
            img: 'https://cdn.magicpatterns.com/uploads/fejejfijHgvKJz95cjAD13/IMG_20260617_124116.jpg'
          }].map((project, i) => <AnimatedGridItem key={i} className="group" data-id="element-344">
                <div className="aspect-[4/3] overflow-hidden rounded-sm mb-4 bg-slate-100" data-id="element-345">
                  {project.video ? <video src={project.video} autoPlay loop muted playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-346" /> : <img src={project.img} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-347" />}
                </div>
                <h3 className="font-display font-bold text-base text-accent uppercase tracking-wide mb-1" data-id="element-348">
                  {project.title}
                </h3>
                <p className="text-sm text-slate-600 leading-snug" data-id="element-349">
                  {project.desc}
                </p>
              </AnimatedGridItem>)}
          </AnimatedGrid>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-20 md:py-28 bg-white relative" data-id="element-350">
        <div className="max-w-7xl mx-auto px-6" data-id="element-351">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-352">
            <AnimatedSection data-id="element-353">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-navy mb-6 tracking-tight" data-id="element-354">
                Electrical Expertise You Can{' '}
                <span className="text-accent" data-id="element-355">Trust</span>
              </h2>
              <div className="w-20 h-1.5 bg-accent mb-8" data-id="element-356"></div>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-357">
                At The Electrical Professionals Inc, we are committed to
                delivering safe, dependable, and professional electrical
                services for residential and commercial customers.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-358">
                We take pride in quality workmanship, honest communication, and
                reliable solutions that customers can trust.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-accent transition-colors group" data-id="element-359">
                Learn More
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-360" />
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="relative" data-id="element-361">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-2xl relative z-10" data-id="element-362">
                <video src="https://cdn.magicpatterns.com/uploads/a2f3RJtWrdTBQeMErzZHAR/kling_20260618_VIDEO_Create_a_r_72_0.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-363" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-slate-100 rounded-sm z-0 border border-slate-200" data-id="element-364"></div>
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-grid-pattern opacity-10 z-0" data-id="element-365"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW SECTION */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-id="element-366">
        <div className="max-w-7xl mx-auto px-6" data-id="element-367">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16" data-id="element-368">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-navy mb-6 tracking-tight" data-id="element-369">
              Complete Electrical Solutions
            </h2>
            <div className="w-20 h-1.5 bg-accent mx-auto mb-6" data-id="element-370"></div>
          </AnimatedSection>

          <AnimatedGrid className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-371">
            {[{
            title: 'Electrical Repairs',
            icon: Wrench,
            desc: 'Fast, reliable repairs for all electrical issues.'
          }, {
            title: 'Troubleshooting',
            icon: Activity,
            desc: 'Advanced diagnostics to find and fix hidden problems.'
          }, {
            title: 'Panel Upgrades',
            icon: Shield,
            desc: 'Modernize your electrical panel for safety and capacity.'
          }, {
            title: 'Lighting Installation',
            icon: Lightbulb,
            desc: 'Indoor, outdoor, and security lighting solutions.'
          }, {
            title: 'Commercial Electrical',
            icon: Building2,
            desc: 'Comprehensive services for businesses.'
          }, {
            title: 'Residential Electrical',
            icon: HomeIcon,
            desc: 'Complete home electrical services.'
          }, {
            title: 'Emergency Service',
            icon: AlertTriangle,
            desc: '24/7 support for urgent electrical needs.'
          }, {
            title: 'Electrical Maintenance',
            icon: Clock,
            desc: 'Preventative care to ensure reliability.'
          }].map((service, i) => <AnimatedGridItem key={i} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 hover:shadow-md transition-shadow group" data-id="element-372">
                <div className="bg-slate-50 w-12 h-12 rounded-sm flex items-center justify-center mb-4 group-hover:bg-accent group-hover:text-white transition-colors" data-id="element-373">
                  <service.icon className="w-6 h-6 text-navy group-hover:text-white transition-colors" data-id="element-374" />
                </div>
                <h3 className="font-display text-lg font-bold text-navy mb-2" data-id="element-375">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-376">
                  {service.desc}
                </p>
              </AnimatedGridItem>)}
          </AnimatedGrid>

          <AnimatedSection delay={0.3} className="text-center mt-12" data-id="element-377">
            <Link to="/services" className="inline-flex items-center gap-2 bg-navy hover:bg-charcoal text-white px-8 py-4 rounded-sm font-semibold transition-colors group" data-id="element-378">
              View All Services
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-379" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <motion.section initial="dark" whileInView="light" viewport={{
      once: true,
      margin: '-100px'
    }} variants={{
      dark: {
        backgroundColor: '#020308'
      },
      light: {
        backgroundColor: '#050814',
        transition: {
          duration: 1.5,
          delay: 0.8
        }
      }
    }} className="py-20 md:py-28 text-white relative overflow-hidden" data-id="element-380">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" data-id="element-381"></div>

        {/* Animated Radial Glow */}
        <motion.div variants={{
        dark: {
          opacity: 0,
          scale: 0.5
        },
        light: {
          opacity: 1,
          scale: 1,
          transition: {
            duration: 2,
            delay: 0.8
          }
        }
      }} className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-yellow-400/5 rounded-full blur-3xl pointer-events-none" data-id="element-382"></motion.div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-383">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-384">
            <motion.div variants={{
            dark: {
              color: '#1e293b',
              filter: 'drop-shadow(0 0 0px rgba(250, 204, 21, 0))'
            },
            light: {
              color: ['#1e293b', '#facc15', '#1e293b', '#facc15', '#facc15'],
              filter: ['drop-shadow(0 0 0px rgba(250, 204, 21, 0))', 'drop-shadow(0 0 20px rgba(250, 204, 21, 0.8))', 'drop-shadow(0 0 0px rgba(250, 204, 21, 0))', 'drop-shadow(0 0 40px rgba(250, 204, 21, 1))', 'drop-shadow(0 0 60px rgba(250, 204, 21, 0.4))'],
              transition: {
                duration: 0.8,
                times: [0, 0.15, 0.3, 0.45, 1],
                delay: 0.4
              }
            }
          }} data-id="element-385">
              <Lightbulb className="w-16 h-16 mx-auto mb-6" data-id="element-386" />
            </motion.div>

            <motion.h2 variants={{
            dark: {
              opacity: 0.3
            },
            light: {
              opacity: 1,
              transition: {
                duration: 1,
                delay: 0.8
              }
            }
          }} className="font-display text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-387">
              Built on Trust, Powered by Expertise
            </motion.h2>
            <motion.div variants={{
            dark: {
              opacity: 0,
              width: 0
            },
            light: {
              opacity: 1,
              width: 80,
              transition: {
                duration: 0.8,
                delay: 1
              }
            }
          }} className="h-1.5 bg-accent mx-auto mb-8" data-id="element-388"></motion.div>
          </div>

          <AnimatedGrid className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-389">
            {[{
            title: 'Licensed Professionals',
            icon: Shield,
            desc: 'Fully licensed, insured, and highly trained experts.'
          }, {
            title: 'Reliable Service',
            icon: Clock,
            desc: 'We show up on time and complete projects as promised.'
          }, {
            title: 'Safety Focused',
            icon: CheckCircle2,
            desc: 'Strict adherence to all safety codes and standards.'
          }, {
            title: 'Flexible Financing',
            icon: Banknote,
            desc: 'Options available to make your project affordable.'
          }, {
            title: 'Customer Satisfaction',
            icon: Star,
            desc: "We aren't finished until you are completely satisfied."
          }, {
            title: 'Honest Communication',
            icon: ThumbsUp,
            desc: 'Transparent pricing and clear project updates.'
          }].map((feature, i) => <AnimatedGridItem key={i} className="bg-slate-800/50 border border-slate-700 p-8 rounded-sm" data-id="element-390">
                <feature.icon className="w-8 h-8 text-accent mb-4" data-id="element-391" />
                <h3 className="font-display font-bold text-xl mb-2" data-id="element-392">
                  {feature.title}
                </h3>
                <p className="text-slate-400" data-id="element-393">{feature.desc}</p>
              </AnimatedGridItem>)}
          </AnimatedGrid>
        </div>
      </motion.section>

      {/* CTA */}
      <section className="py-20 bg-accent text-white" data-id="element-394">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-395">
          <AnimatedSection data-id="element-396">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-397">
              Need Electrical Service?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-398">
              Request Your Free Quote Today
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-399">
              <Link to="/quote" className="bg-navy hover:bg-slate-800 text-white px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-400">
                Request a Free Quote
              </Link>
              <a href="tel:4075040596" className="bg-white text-accent hover:bg-slate-50 px-8 py-4 rounded-sm font-bold text-lg transition-colors flex items-center justify-center gap-2" data-id="element-401">
                <Phone className="w-5 h-5" data-id="element-402" />
                (407) 504-0596
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}